import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MsvSelect.css';

const filterOptions = (options, query) => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
        (opt) =>
            String(opt.label).toLowerCase().includes(q) ||
            String(opt.value).toLowerCase().includes(q)
    );
};

const resolveFilterCommit = (options, rawQuery, currentValue) => {
    const q = rawQuery.trim().toLowerCase();
    if (!q) {
        if (options.some((opt) => opt.value === '')) return '';
        return currentValue;
    }
    const exact = options.find(
        (opt) =>
            String(opt.label).toLowerCase() === q ||
            String(opt.value).toLowerCase() === q
    );
    if (exact) return exact.value;
    const starts = options.filter(
        (opt) =>
            String(opt.label).toLowerCase().startsWith(q) ||
            String(opt.value).toLowerCase().startsWith(q)
    );
    if (starts.length === 1) return starts[0].value;
    return currentValue;
};

/**
 * Custom select — light MSV menu instead of the native OS dark popup.
 * options: [{ value, label }]
 * filterable: type to filter the dropdown in real time (combobox).
 */
const MsvSelect = ({
    id,
    label,
    value,
    onChange,
    options = [],
    name,
    className = '',
    fullWidth = false,
    placeholder = 'Select…',
    disabled = false,
    filterable = false,
    'aria-label': ariaLabel,
}) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const [query, setQuery] = useState('');
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const reactId = useId();
    const labelId = id ? `${id}-label` : `${reactId}-label`;
    const listId = id ? `${id}-list` : `${reactId}-list`;

    const selected = options.find((opt) => String(opt.value) === String(value));
    const displayLabel = selected?.label ?? placeholder;
    const menuOptions = filterable ? filterOptions(options, query) : options;

    const syncQueryFromValue = () => {
        setQuery(selected ? String(selected.label) : '');
    };

    useEffect(() => {
        if (!filterable || open) return;
        syncQueryFromValue();
    }, [filterable, open, value, selected?.label]);

    const updateMenuPosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const gap = 6;
        const maxMenuHeight = Math.min(280, window.innerHeight * 0.5);
        const spaceBelow = window.innerHeight - rect.bottom - gap - 8;
        const spaceAbove = rect.top - gap - 8;
        const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
        const width = Math.max(rect.width, 160);

        let left = rect.left;
        if (left + width > window.innerWidth - 12) {
            left = Math.max(12, window.innerWidth - width - 12);
        }

        setMenuStyle({
            position: 'fixed',
            left,
            width,
            minWidth: width,
            maxWidth: width,
            maxHeight: Math.min(maxMenuHeight, openUp ? spaceAbove : spaceBelow),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + gap, top: 'auto' }
                : { top: rect.bottom + gap, bottom: 'auto' }),
            zIndex: 10100,
        });
    };

    useLayoutEffect(() => {
        if (!open) {
            setMenuStyle(null);
            return undefined;
        }
        updateMenuPosition();
        const onReposition = () => updateMenuPosition();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [open, query, menuOptions.length]);

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (e) => {
            const inTrigger = rootRef.current?.contains(e.target);
            const inMenu = menuRef.current?.contains(e.target);
            if (!inTrigger && !inMenu) {
                setOpen(false);
                if (filterable) syncQueryFromValue();
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                if (filterable) syncQueryFromValue();
            }
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, filterable, selected?.label]);

    const commit = (nextValue) => {
        setOpen(false);
        setHighlightIndex(-1);
        if (!onChange) return;
        if (name) {
            onChange({ target: { name, value: nextValue } });
        } else {
            onChange(nextValue);
        }
        if (filterable) {
            const nextSelected = options.find((opt) => String(opt.value) === String(nextValue));
            setQuery(nextSelected ? String(nextSelected.label) : '');
        }
    };

    const handleFilterFocus = () => {
        if (disabled) return;
        setOpen(true);
        setHighlightIndex(-1);
        window.requestAnimationFrame(() => triggerRef.current?.select());
    };

    const handleFilterChange = (event) => {
        setQuery(event.target.value);
        setOpen(true);
        setHighlightIndex(-1);
    };

    const handleFilterBlur = () => {
        window.setTimeout(() => {
            if (menuRef.current?.contains(document.activeElement)) return;
            setOpen(false);
            setHighlightIndex(-1);
            const nextValue = resolveFilterCommit(options, query, value);
            if (String(nextValue) !== String(value)) {
                commit(nextValue);
                return;
            }
            syncQueryFromValue();
        }, 120);
    };

    const handleFilterKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setHighlightIndex((prev) => Math.min(prev + 1, menuOptions.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setOpen(true);
            setHighlightIndex((prev) => Math.max(prev - 1, 0));
            return;
        }
        if (event.key === 'Enter' && open) {
            event.preventDefault();
            if (highlightIndex >= 0 && menuOptions[highlightIndex]) {
                commit(menuOptions[highlightIndex].value);
                return;
            }
            if (menuOptions.length === 1) {
                commit(menuOptions[0].value);
                return;
            }
            const nextValue = resolveFilterCommit(options, query, value);
            if (String(nextValue) !== String(value)) {
                commit(nextValue);
            } else {
                setOpen(false);
                syncQueryFromValue();
            }
        }
    };

    const menu = open && menuStyle
        ? createPortal(
            <ul
                className="msv-select-menu is-portal"
                role="listbox"
                id={listId}
                ref={menuRef}
                style={menuStyle}
                aria-labelledby={label ? labelId : undefined}
            >
                {menuOptions.length === 0 ? (
                    <li className="msv-select-empty" role="presentation">
                        No matches
                    </li>
                ) : (
                    menuOptions.map((opt, index) => {
                        const isSelected = String(opt.value) === String(value);
                        const isHighlighted = filterable && index === highlightIndex;
                        return (
                            <li key={String(opt.value)} role="option" aria-selected={isSelected}>
                                <button
                                    type="button"
                                    className={`msv-select-option${isSelected ? ' is-selected' : ''}${isHighlighted ? ' is-highlighted' : ''}`}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => commit(opt.value)}
                                >
                                    {opt.label}
                                </button>
                            </li>
                        );
                    })
                )}
            </ul>,
            document.body
        )
        : null;

    return (
        <div
            className={`msv-select${fullWidth ? ' is-full' : ''}${filterable ? ' is-filterable' : ''}${className ? ` ${className}` : ''}`}
            ref={rootRef}
        >
            {label ? (
                <span className="msv-select-label" id={labelId}>
                    {label}
                </span>
            ) : null}
            <div className="msv-select-control">
                {filterable ? (
                    <input
                        type="text"
                        id={id}
                        ref={triggerRef}
                        className={`msv-select-trigger msv-select-filter-input${open ? ' is-open' : ''}${!selected ? ' is-placeholder' : ''}`}
                        value={query}
                        onChange={handleFilterChange}
                        onFocus={handleFilterFocus}
                        onBlur={handleFilterBlur}
                        onKeyDown={handleFilterKeyDown}
                        disabled={disabled}
                        placeholder={placeholder}
                        autoComplete="off"
                        spellCheck={false}
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={open}
                        aria-labelledby={label ? labelId : undefined}
                        aria-label={!label ? ariaLabel || placeholder : undefined}
                        aria-controls={open ? listId : undefined}
                    />
                ) : (
                    <button
                        type="button"
                        id={id}
                        ref={triggerRef}
                        className={`msv-select-trigger${open ? ' is-open' : ''}${!selected ? ' is-placeholder' : ''}`}
                        onClick={() => !disabled && setOpen((v) => !v)}
                        disabled={disabled}
                        aria-haspopup="listbox"
                        aria-expanded={open}
                        aria-labelledby={label ? labelId : undefined}
                        aria-label={!label ? ariaLabel || placeholder : undefined}
                        aria-controls={open ? listId : undefined}
                    >
                        <span className="msv-select-value">{displayLabel}</span>
                    </button>
                )}
            </div>
            {menu}
        </div>
    );
};

export default MsvSelect;
